/**
 * Pure presentation rules for the Briefs page. Kept out of the component so
 * what a user is told — especially about failed, skipped and partial
 * sections — is testable without rendering.
 */
import { BriefsApiError } from '../services/briefsApi';
import type {
  AlertCondition, BriefDefinition, BriefProposal, BriefRun, DraftMissingField, BriefResultItem, BriefSchedule, BriefSectionResult,
  PeriodPreset, RunStatus, SectionStatus, SendReason, WindowSpec,
} from '../types/briefs';

/** The Cognito `custom:permissions` value that grants the Briefs page and API. */
export const BRIEFS_PERMISSION = 'zoe-briefs';

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** "every 15 minutes, 06:00–18:00" for an alert that checks through the day. */
function repeatText(s: Pick<BriefSchedule, 'every_minutes' | 'until' | 'local_time'>): string {
  const every = s.every_minutes === 60 ? 'every hour' : `every ${s.every_minutes} minutes`;
  if (!s.local_time) return every;
  if (s.until) return `${every}, ${s.local_time}–${s.until}`;
  return s.local_time === '00:00' ? `${every}, all day` : `${every} from ${s.local_time}`;
}

export function scheduleSummary(s: BriefSchedule): string {
  if (s.frequency === 'on_data') return 'When new data arrives';
  const when =
    s.frequency === 'daily' ? 'Every day'
      : s.frequency === 'weekdays' ? 'Weekdays'
        : `Every ${DAY_NAMES[s.day_of_week ?? 1] ?? 'week'}`;
  if (s.every_minutes) return `${when}, ${repeatText(s)} (${s.timezone})`;
  return `${when} at ${s.local_time} (${s.timezone})`;
}

export const PRESET_LABELS: Record<PeriodPreset, string> = {
  yesterday: 'Yesterday',
  today_so_far: 'Today so far',
  last_7_days: 'Last 7 days',
  last_30_days: 'Last 30 days',
  week_to_date: 'Week to date',
  month_to_date: 'Month to date',
  last_working_week: 'Last week (Mon–Fri)',
  last_month: 'Last month',
};

export function windowLabel(w: WindowSpec): string {
  if (w.kind === 'snapshot') return 'Right now';
  if (w.kind === 'horizon') return w.forward_days === 0 ? 'Rest of today' : `Next ${w.forward_days} days`;
  if ('preset' in w) return PRESET_LABELS[w.preset];
  if ('days_ago' in w) return `${w.days_ago} days ago`;
  return `Last ${w.rolling_days} days`;
}

export function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/London';
  } catch {
    return 'Europe/London';
  }
}

export const STATUS_LABELS: Record<SectionStatus, string> = {
  ok: 'Complete',
  partial: 'Partial',
  insufficient_data: 'Not enough data',
  failed: 'Failed',
  skipped: 'Not shown',
};

export type Tone = 'good' | 'warn' | 'bad' | 'muted';
export function statusTone(s: SectionStatus | RunStatus): Tone {
  if (s === 'ok') return 'good';
  if (s === 'partial' || s === 'insufficient_data') return 'warn';
  if (s === 'failed') return 'bad';
  return 'muted';
}

/** Why a section shows nothing. Never phrased as if the answer were zero. */
export function sectionExplanation(s: BriefSectionResult): string | null {
  if (s.status === 'failed') return s.error?.message ?? 'This section could not be produced.';
  if (s.status === 'skipped') {
    switch (s.skip_reason) {
      case 'unauthorised': return 'You don’t have access to this section’s data.';
      case 'unreconstructable_snapshot': return 'This ran too late to show the state at its scheduled time.';
      case 'horizon_elapsed': return 'The period this looks ahead to had already passed when it ran.';
      case 'deleted': return 'This section’s source has been removed.';
      default: return 'This section was not run.';
    }
  }
  if (s.status === 'insufficient_data') return 'There isn’t enough data to show this section — see below.';
  return null;
}

/** When the numbers are for. Snapshot → the instant observed; period → the interval. */
export function intervalText(s: BriefSectionResult, tz?: string): string {
  const fmt = (iso: string) =>
    new Date(iso).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short', ...(tz ? { timeZone: tz } : {}) });
  const r = s.resolved_interval;
  if (r.kind === 'snapshot') return `As of ${fmt(r.observed_at)}`;
  if (r.kind === 'horizon') return `From ${fmt(r.observed_at)} to ${fmt(r.until)}`;
  return `${fmt(r.from)} to ${fmt(r.to)}`;
}

/** Mirrors the server's NOTICES registry; an unknown code is shown as-is rather than guessed at. */
const NOTICE_TEXT: Record<string, string> = {
  confirm_walkaround_checks: 'Confirm walkaround checks are complete before vehicles depart.',
  review_before_dispatch: 'Review the items below before dispatch.',
  preview_not_saved: 'This is a preview. It is not saved to run history and no notifications are sent.',
  first_run_no_comparison: 'This is the first run of this brief, so there is no earlier run to compare against.',
};
export function noticeText(code: string): string {
  return NOTICE_TEXT[code] ?? code;
}

export function formatFigure(item: Extract<BriefResultItem, { kind: 'figure' }>): string {
  return item.value.n.toLocaleString('en-GB');
}

/** How an item is evidenced, in words. */
export function evidenceText(item: BriefResultItem): string | null {
  if (item.kind === 'notice') return null;
  const n = item.evidence_refs.length;
  // Never imply a computed value links to records.
  if (n > 0 && item.evidence_basis === 'query') return 'Computed by a query';
  if (n > 0) return `Based on ${n} recorded ${n === 1 ? 'event' : 'events'}`;
  if (item.kind === 'figure' && item.coverage) return `Searched ${item.coverage.searched}`;
  return null;
}

/** A Zoe draft's schedule, which may still be missing parts. */
export function draftScheduleSummary(s: BriefProposal['schedule']): string {
  if (s.frequency === 'on_data') return 'When new data arrives, not on a schedule';
  const when =
    s.frequency === 'daily' ? 'Every day'
      : s.frequency === 'weekdays' ? 'Weekdays'
        : s.frequency === 'weekly'
          ? s.day_of_week !== undefined ? `Every ${DAY_NAMES[s.day_of_week]}` : 'Weekly, day not chosen'
          : 'How often not chosen';
  if (s.every_minutes) return `${when}, ${repeatText(s as BriefSchedule)} (${s.timezone})`;
  return `${when}${s.local_time ? ` at ${s.local_time}` : ', time not chosen'} (${s.timezone})`;
}

const MISSING_TEXT: Record<DraftMissingField, string> = {
  content: 'what to report',
  answers: 'answers to the questions above',
  condition: 'when the alert should be sent, as something the data can check',
  frequency: 'how often',
  local_time: 'what time',
  day_of_week: 'which day',
};
export const missingText = (m: DraftMissingField[]): string => m.map((f) => MISSING_TEXT[f]).join(', ');

/** What the user is told when Create or Cancel is refused. Never phrased as if it worked. */
export function draftErrorMessage(err: unknown): string {
  if (!(err instanceof BriefsApiError)) return 'Something went wrong. Please try again.';
  const body = err.body ?? {};
  switch (err.status) {
    case 409:
      if (body.draft) return 'This draft has changed since this card was shown. Use the latest version further down.';
      return typeof body.error === 'string' ? `${body.error.charAt(0).toUpperCase()}${body.error.slice(1)}.` : 'This draft can no longer be changed.';
    case 410:
      return 'This draft has expired. Ask Zoe for a new one.';
    case 403:
      return 'You no longer have access to one or more of these sections.';
    case 422:
      return 'The draft is not complete yet. Tell Zoe what is missing.';
    case 404:
      return 'This draft no longer exists. Ask Zoe for a new one.';
    default:
      return err.message;
  }
}

/** Who made and last changed an organisation brief, by the label recorded at the time. */
export function authorshipText(b: Pick<BriefDefinition, 'created_by' | 'created_by_label' | 'updated_by' | 'updated_by_label' | 'revision'>): string {
  const who = (label?: string) => label || 'a colleague';
  const created = `Created by ${who(b.created_by_label)}`;
  if (b.revision <= 1 || b.updated_by === b.created_by) return created;
  return `${created} · last changed by ${who(b.updated_by_label)}`;
}

const fmtWhen = (iso: string, tz?: string) =>
  new Date(iso).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short', ...(tz ? { timeZone: tz } : {}) });

/**
 * Why a scheduled run shows no sections, in words. Null for a run whose
 * sections tell their own story. Never phrased as if the answer were zero.
 */
export function runExplanation(run: Pick<BriefRun, 'skip_reason' | 'coalesced_range' | 'failure'>, tz?: string): string | null {
  if (run.failure) return 'The scheduler could not complete this run after several attempts, so nothing was measured for it.';
  if (run.skip_reason === 'zoe_disabled') return 'Not run: Zoe was switched off for your organisation at the scheduled time.';
  if (run.skip_reason === 'stale' && run.coalesced_range) {
    const { from, to, count } = run.coalesced_range;
    return count === 1
      ? `Not run: it was too late to run the brief scheduled for ${fmtWhen(from, tz)}.`
      : `Not run: ${count} scheduled runs between ${fmtWhen(from, tz)} and ${fmtWhen(to, tz)} were missed and were too late to run.`;
  }
  return null;
}

const OP_TEXT: Record<AlertCondition['op'], string> = {
  '>': 'more than', '>=': 'at least', '<': 'less than', '<=': 'at most', '=': 'exactly', '!=': 'anything but',
};

/**
 * When an alert sends, in words: the user's own condition, then how it is
 * checked. Mirrors zeecore `describeCondition`. Null for a brief.
 */
export function alertSummary(b: Pick<BriefDefinition, 'condition' | 'condition_label'>): string | null {
  const c = b.condition;
  if (!c) return null;
  const measured = `${OP_TEXT[c.op]} ${c.threshold}${c.count_of ? ' rows' : ''}`;
  const when = c.send_when === 'new_items' ? 'sent when new ones appear' : 'sent when it becomes true';
  const what = b.condition_label ? `when ${b.condition_label} ` : '';
  return `Sends only ${what}(checked as: ${measured}; ${when}${c.all_clear ? ', and when it clears' : ''})`;
}

const SEND_REASON_TEXT: Record<SendReason, string> = {
  became_true: 'the condition became true',
  new_items: 'new items appeared',
  all_clear: 'the condition cleared',
  still_true: 'still true, nothing new since the last send',
  not_met: 'the condition was not met',
  unreadable: 'the data could not be read, so nothing was decided',
};

/**
 * What an alert run did, in words. Only scheduled runs send; a preview or a
 * run started by hand says what it would have done and changes nothing.
 */
export function alertRunText(run: Pick<BriefRun, 'trigger' | 'alert' | 'data_trigger'>): string | null {
  const a = run.alert;
  if (!a) return null;
  const why = SEND_REASON_TEXT[a.condition.reason];
  const health = a.health === 'cannot_check'
    ? ' It has now failed to check its data several times in a row, so the people it notifies were told it can\'t check.'
    : a.health === 'checking_again' ? ' It can check its data again, and the people it notifies were told.' : '';
  const early = run.data_trigger ? ` Checked because new ${dataKinds(run.data_trigger.event_types)} data arrived.` : '';
  if (run.trigger === 'scheduled') return `${a.sent ? `Sent: ${why}.` : `Not sent: ${why}.`}${early}${health}`;
  return a.condition.send
    ? `Would send (${why}). Only scheduled runs send, and this run changes nothing.`
    : `Would not send: ${why}.`;
}

/** One-word status for an alert's history row. */
export function alertRunBadge(a: { sent: boolean; reason: SendReason }, trigger: BriefRun['trigger']): string {
  if (trigger !== 'scheduled') return 'test';
  if (a.sent) return 'sent';
  return a.reason === 'unreadable' ? 'could not check' : 'not sent';
}

/** "Next run …" for an enabled brief, in the brief's own timezone. */
export function nextRunText(b: Pick<BriefDefinition, 'enabled' | 'next_occurrence_due' | 'schedule'>): string | null {
  if (b.enabled && !b.next_occurrence_due && b.schedule.frequency === 'on_data') return 'Next check when new data arrives';
  if (!b.enabled || !b.next_occurrence_due) return null;
  return `Next run ${fmtWhen(b.next_occurrence_due, b.schedule.timezone)}`;
}

/** What kind of data an event type is, for "checks when new … arrives". */
const DATA_NAMES: Record<string, string> = {
  VorAssetObserved: 'VOR',
  DefectObserved: 'defect',
  DefectCheckObserved: 'walkaround check',
  DriverShiftObserved: 'driver shift',
  DriverViolationObserved: 'driver-hours violation',
  FleetComplianceBandObserved: 'compliance',
  FuelTransactionObserved: 'fuel',
  FuelTransactionDeclared: 'fuel',
  VehiclePositionObserved: 'vehicle position',
};

/** "VOR or defect", from event types; unknown types are shown as they are. */
export function dataKinds(eventTypes: readonly string[]): string {
  const names = [...new Set(eventTypes.map((t) => DATA_NAMES[t] ?? t))];
  return names.length <= 1 ? names.join('') : `${names.slice(0, -1).join(', ')} or ${names[names.length - 1]}`;
}
